import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';
import { useAppStore } from '../store';
import {
  Users,
  DollarSign,
  Plus,
  Edit,
  RefreshCw,
  AlertTriangle,
  Check,
  Download,
  Calendar,
  Trash2,
  TrendingUp,
  Eye,
} from 'lucide-react';
import { format, parseISO, addMonths, differenceInCalendarDays, startOfMonth, endOfMonth, isBefore, isAfter } from 'date-fns';
import { es } from 'date-fns/locale';
import { formatCurrency } from '../utils/formatters';
import { objectsToCsv, downloadCsv } from '../utils/csvUtils';
import Button from '../components/ui/Button';
import Card, { CardHeader, CardContent } from '../components/ui/Card';
import EmployeeModal, { type EmployeeFormData } from '../components/modals/EmployeeModal';
import MarkPaidModal from '../components/modals/MarkPaidModal';
import AddVacationModal from '../components/modals/AddVacationModal';
import EditSalaryPaymentModal from '../components/modals/EditSalaryPaymentModal';
import CreateSalaryPreviewModal from '../components/modals/CreateSalaryPreviewModal';
import DeleteConfirmModal from '../components/modals/DeleteConfirmModal';
import BulkMarkPaidModal from '../components/modals/BulkMarkPaidModal';
import CommissionDetailModal, { type CommissionInvoice } from '../components/modals/CommissionDetailModal';

const isAdmin = (role: string | null) => role === 'Administrador' || role === 'Super Admin';

interface EmployeeProfile {
  id: string;
  user_id: string | null;
  name: string;
  email: string | null;
  role: string;
  base_salary: number;
  commission_percent: number;
  salary_review_frequency_months: number;
  last_salary_review_at: string | null;
  vacation_days_per_year: number;
  is_active: boolean;
  is_independent?: boolean;
  invoiced_amount?: number;
  commission_amount?: number;
}

interface SalaryPayment {
  id: string;
  employee_profile_id: string;
  period_start: string;
  period_end: string;
  base_amount: number;
  commission_amount: number;
  total_amount: number;
  status: string;
  paid_at: string | null;
  expense_id?: string | null;
  employee_name?: string;
}

interface SalaryReviewDue {
  id: string;
  name: string;
  last_salary_review_at: string | null;
  salary_review_frequency_months: number;
  next_review_due: string;
}

interface VacationEntry {
  id: string;
  employee_profile_id: string;
  start_date: string;
  end_date: string;
  days_taken: number;
  employee_name?: string;
}

const Colaboradores: React.FC = () => {
  const { userRole } = useAuth();
  const { fetchExpenses, fetchExpenseCategories } = useAppStore();
  const [activeTab, setActiveTab] = useState<'empleados' | 'sueldos' | 'comisiones' | 'vacaciones' | 'descargar'>('empleados');
  const [loading, setLoading] = useState(true);
  const [employees, setEmployees] = useState<EmployeeProfile[]>([]);
  const [salaryPayments, setSalaryPayments] = useState<SalaryPayment[]>([]);
  const [salaryReviewDue, setSalaryReviewDue] = useState<SalaryReviewDue[]>([]);
  const [vacationEntries, setVacationEntries] = useState<VacationEntry[]>([]);
  const [vacationYear, setVacationYear] = useState(() => new Date().getFullYear());
  const [commissionYear, setCommissionYear] = useState(() => new Date().getFullYear());
  const [commissionEmployees, setCommissionEmployees] = useState<EmployeeProfile[]>([]);
  const [commissionLoading, setCommissionLoading] = useState(false);
  const [commissionDetailModal, setCommissionDetailModal] = useState<{
    show: boolean;
    employee: EmployeeProfile | null;
  }>({ show: false, employee: null });
  const [commissionDetailInvoices, setCommissionDetailInvoices] = useState<CommissionInvoice[]>([]);
  const [commissionDetailLoading, setCommissionDetailLoading] = useState(false);
  const [addVacationModal, setAddVacationModal] = useState(false);
  const [editVacationModal, setEditVacationModal] = useState<VacationEntry | null>(null);
  const [deleteVacationModal, setDeleteVacationModal] = useState<VacationEntry | null>(null);
  const [vacationDaysTakenByEmployee, setVacationDaysTakenByEmployee] = useState<Record<string, number>>({});
  const [periodStart, setPeriodStart] = useState(() => {
    const d = new Date();
    d.setDate(1);
    return d.toISOString().slice(0, 10);
  });
  const [periodEnd, setPeriodEnd] = useState(() => {
    const d = new Date();
    return d.toISOString().slice(0, 10);
  });
  const [reportStart, setReportStart] = useState(() => {
    const d = new Date();
    d.setDate(1);
    return d.toISOString().slice(0, 10);
  });
  const [reportEnd, setReportEnd] = useState(() => new Date().toISOString().slice(0, 10));

  const [employeeModal, setEmployeeModal] = useState<{ show: boolean; editing: EmployeeProfile | null }>({ show: false, editing: null });
  const [markPaidModal, setMarkPaidModal] = useState<{ show: boolean; payment: SalaryPayment | null }>({ show: false, payment: null });
  const [editSalaryModal, setEditSalaryModal] = useState<{ show: boolean; payment: SalaryPayment | null }>({ show: false, payment: null });
  const [deleteSalaryModal, setDeleteSalaryModal] = useState<{ show: boolean; payment: SalaryPayment | null }>({ show: false, payment: null });
  const [createSalaryPreviewModal, setCreateSalaryPreviewModal] = useState<{ show: boolean; employee: EmployeeProfile | null }>({ show: false, employee: null });
  const [selectedPayments, setSelectedPayments] = useState<Set<string>>(new Set());
  const [bulkMarkPaidModal, setBulkMarkPaidModal] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isAdmin(userRole)) loadData();
  }, [userRole, periodStart, periodEnd]);


  useEffect(() => {
    if (isAdmin(userRole) && activeTab === 'vacaciones') loadVacations();
  }, [userRole, activeTab, vacationYear]);

  useEffect(() => {
    if (isAdmin(userRole) && activeTab === 'comisiones') loadCommissionYear();
  }, [userRole, activeTab, commissionYear]);

  const loadCommissionYear = async () => {
    setCommissionLoading(true);
    try {
      const start = `${commissionYear}-01-01`;
      const end = `${commissionYear}-12-31`;
      const { data, error } = await supabase.rpc('hr_list_employees_with_sales', {
        p_start_date: start,
        p_end_date: end,
      });
      if (error) throw error;
      setCommissionEmployees(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error(e);
      setCommissionEmployees([]);
    } finally {
      setCommissionLoading(false);
    }
  };

  const openCommissionDetail = async (emp: EmployeeProfile) => {
    setCommissionDetailModal({ show: true, employee: emp });
    setCommissionDetailLoading(true);
    setCommissionDetailInvoices([]);
    try {
      const { data, error } = await supabase.rpc('hr_commission_invoice_details', {
        p_employee_profile_id: emp.id,
        p_start_date: `${commissionYear}-01-01`,
        p_end_date: `${commissionYear}-12-31`,
      });
      if (error) throw error;
      const d = data as { invoices?: CommissionInvoice[] } | null;
      setCommissionDetailInvoices(Array.isArray(d?.invoices) ? d.invoices : []);
    } catch (e) {
      console.error(e);
      setCommissionDetailInvoices([]);
    } finally {
      setCommissionDetailLoading(false);
    }
  };

  // Matriz vacaciones: empleado x mes → días en ese mes
  const vacationMatrix = useMemo(() => {
    const activeEmps = employees.filter((e) => e.is_active).sort((a, b) => a.name.localeCompare(b.name));
    const matrix: Record<string, Record<number, number>> = {};
    activeEmps.forEach((emp) => {
      matrix[emp.id] = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0, 6: 0, 7: 0, 8: 0, 9: 0, 10: 0, 11: 0, 12: 0 };
    });
    vacationEntries.forEach((ve) => {
      const start = parseISO(ve.start_date);
      const end = parseISO(ve.end_date);
      const empId = ve.employee_profile_id;
      if (!matrix[empId]) return;
      for (let m = 1; m <= 12; m++) {
        const monthStart = startOfMonth(new Date(vacationYear, m - 1, 1));
        const monthEnd = endOfMonth(new Date(vacationYear, m - 1, 1));
        const rangeStart = isAfter(start, monthStart) ? start : monthStart;
        const rangeEnd = isBefore(end, monthEnd) ? end : monthEnd;
        if (rangeStart <= rangeEnd) {
          const days = differenceInCalendarDays(rangeEnd, rangeStart) + 1;
          matrix[empId][m] = (matrix[empId][m] || 0) + days;
        }
      }
    });
    return { matrix, employees: activeEmps };
  }, [vacationEntries, vacationYear, employees]);

  const metricCards = useMemo(() => {
    const periodPayments = salaryPayments.filter((sp) => sp.period_start <= periodEnd && sp.period_end >= periodStart);
    const pendingPayments = salaryPayments.filter((sp) => sp.status === 'pending');
    const paidPayments = salaryPayments.filter((sp) => sp.status === 'paid');
    const totalNómina = periodPayments.reduce((s, p) => s + (p.total_amount || 0), 0);
    const pendientes = pendingPayments.reduce((s, p) => s + (p.total_amount || 0), 0);
    const pagados = paidPayments.reduce((s, p) => s + (p.total_amount || 0), 0);
    return {
      totalNómina,
      pendientes,
      pagados,
      totalEmpleados: employees.filter((e) => e.is_active).length,
      pagosPendientes: pendingPayments.length,
      pagosPagados: paidPayments.length,
      pagosEnPeríodo: periodPayments.length,
    };
  }, [salaryPayments, periodStart, periodEnd, employees]);

  const loadVacations = async () => {
    try {
      const start = `${vacationYear}-01-01`;
      const end = `${vacationYear}-12-31`;
      const { data } = await supabase
        .from('employee_vacation_entries')
        .select('id, employee_profile_id, start_date, end_date, days_taken')
                .lte('start_date', end)
        .gte('end_date', start);
      const entries = data || [];
      const profilesMap = new Map<string, string>();
      (await supabase.from('employee_profiles').select('id, name, vacation_days_per_year')).data?.forEach((p: any) => profilesMap.set(p.id, p.name));
      setVacationEntries(entries.map((e: any) => ({ ...e, employee_name: profilesMap.get(e.employee_profile_id) || '—' })));
      // Actualizar días tomados para año actual (empleados tab)
      const currentYear = new Date().getFullYear();
      const yStart = `${currentYear}-01-01`;
      const yEnd = `${currentYear}-12-31`;
      const currentYearEntries = entries.filter(
        (e: any) => !(e.end_date < yStart || e.start_date > yEnd)
      );
      const vacMap: Record<string, number> = {};
      currentYearEntries.forEach((r: any) => {
        vacMap[r.employee_profile_id] = (vacMap[r.employee_profile_id] || 0) + Number(r.days_taken || 0);
      });
      setVacationDaysTakenByEmployee(vacMap);
    } catch (e) {
      console.error(e);
    }
  };

  const handleAddVacation = async (employeeId: string, startDate: string, endDate: string, daysTaken: number) => {
    await supabase.from('employee_vacation_entries').insert({
      employee_profile_id: employeeId,
      start_date: startDate,
      end_date: endDate,
      days_taken: daysTaken,
    });
    setAddVacationModal(false);
    loadVacations();
  };

  const handleEditVacation = async (id: string, employeeId: string, startDate: string, endDate: string, daysTaken: number) => {
    await supabase.from('employee_vacation_entries').update({
      employee_profile_id: employeeId,
      start_date: startDate,
      end_date: endDate,
      days_taken: daysTaken,
    }).eq('id', id);
    setEditVacationModal(null);
    loadVacations();
  };

  const handleDeleteVacation = async () => {
    const entry = deleteVacationModal;
    if (!entry) return;
    setSaving(true);
    try {
      await supabase.from('employee_vacation_entries').delete().eq('id', entry.id);
      setDeleteVacationModal(null);
      loadVacations();
    } catch (e: any) {
      alert(e.message || 'Error al eliminar');
    } finally {
      setSaving(false);
    }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      await supabase.rpc('hr_sync_employee_profiles_from_users');
      fetchExpenseCategories();

      // Empleados y comisiones: usar el período seleccionado (periodStart/periodEnd)
      const [empRes, spRes, profilesRes, reviewRes] = await Promise.all([
        supabase.rpc('hr_list_employees_with_sales', { p_start_date: periodStart, p_end_date: periodEnd }),
        supabase.from('salary_payments').select('*').order('period_start', { ascending: false }).limit(200),
        supabase.from('employee_profiles').select('id, name, is_independent'),
        supabase.rpc('hr_salary_review_due'),
      ]);

      const profilesMap = new Map<string, string>();
      (profilesRes.data || []).forEach((p: any) => profilesMap.set(p.id, p.name));

      if (empRes.data) setEmployees(Array.isArray(empRes.data) ? empRes.data : []);
      if (spRes.data) {
        setSalaryPayments((spRes.data as any[]).map((r) => ({
          ...r,
          employee_name: profilesMap.get(r.employee_profile_id) || '—',
        })));
      }
      if (reviewRes.data) setSalaryReviewDue(Array.isArray(reviewRes.data) ? reviewRes.data : []);
      const y = new Date().getFullYear();
      const { data: vacData } = await supabase.from('employee_vacation_entries').select('employee_profile_id, days_taken').gte('start_date', `${y}-01-01`).lte('end_date', `${y}-12-31`);
      const vacMap: Record<string, number> = {};
      (vacData || []).forEach((r: any) => {
        vacMap[r.employee_profile_id] = (vacMap[r.employee_profile_id] || 0) + Number(r.days_taken || 0);
      });
      setVacationDaysTakenByEmployee(vacMap);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveEmployee = async (formData: EmployeeFormData) => {
    if (!formData.name?.trim()) {
      alert('El nombre es obligatorio');
      return;
    }
    setSaving(true);
    try {
      const lastReview = formData.last_salary_review_at ? `${formData.last_salary_review_at}-01` : null;
      const payload: Record<string, any> = {
        name: formData.name.trim(),
        role: formData.role || 'Otro',
        base_salary: Number(formData.base_salary) || 0,
        salary_review_frequency_months: Number(formData.salary_review_frequency_months) || 3,
        last_salary_review_at: lastReview,
        vacation_days_per_year: Number(formData.vacation_days_per_year) || 14,
        is_active: formData.is_active ?? true,
        is_independent: formData.is_independent ?? false,
        updated_at: new Date().toISOString(),
      };
      if (employeeModal.editing?.user_id) {
        payload.email = formData.email || employeeModal.editing.email;
        payload.commission_percent = Number(formData.commission_percent) || 0;
      } else {
        payload.email = formData.email?.trim() || null;
      }
      if (employeeModal.editing) {
        await supabase.from('employee_profiles').update(payload).eq('id', employeeModal.editing.id);
      } else {
        await supabase.from('employee_profiles').insert({
          ...payload,
          user_id: null,
          commission_percent: 0,
          updated_at: undefined,
        });
      }
      setEmployeeModal({ show: false, editing: null });
      loadData();
    } catch (e: any) {
      alert(e.message || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleMarkPaid = async (paymentMethod: string, paymentDate: string) => {
    if (!markPaidModal.payment) return;
    setSaving(true);
    try {
      const { data, error } = await supabase.rpc('hr_mark_salary_paid', {
        p_salary_payment_id: markPaidModal.payment.id,
        p_payment_method: paymentMethod,
        p_payment_date: paymentDate,
      });
      if (error) throw error;
      const result = data as { success: boolean; error?: string };
      if (!result.success) throw new Error(result.error);
      setMarkPaidModal({ show: false, payment: null });
      fetchExpenses();
      loadData();
    } catch (e: any) {
      alert(e.message || 'Error al marcar como pagado');
    } finally {
      setSaving(false);
    }
  };

  const handleBulkMarkPaid = async (paymentMethod: string, paymentDate: string) => {
    const pendingIds = Array.from(selectedPayments).filter((id) => salaryPayments.find((sp) => sp.id === id)?.status === 'pending');
    setSaving(true);
    try {
      for (const id of pendingIds) {
        const { data, error } = await supabase.rpc('hr_mark_salary_paid', {
          p_salary_payment_id: id,
          p_payment_method: paymentMethod,
          p_payment_date: paymentDate,
        });
        if (error) throw error;
        const result = data as { success: boolean; error?: string };
        if (!result.success) throw new Error(result.error);
      }
      setSelectedPayments(new Set());
      setBulkMarkPaidModal(false);
      fetchExpenses();
      loadData();
    } catch (e: any) {
      alert(e.message || 'Error al marcar como pagado');
    } finally {
      setSaving(false);
    }
  };

  const handleEditSalaryPayment = async (id: string, baseAmount: number, commissionAmount: number) => {
    setSaving(true);
    try {
      await supabase.from('salary_payments').update({
        base_amount: baseAmount,
        commission_amount: commissionAmount,
        total_amount: baseAmount + commissionAmount,
        updated_at: new Date().toISOString(),
      }).eq('id', id).eq('status', 'pending');
      setEditSalaryModal({ show: false, payment: null });
      loadData();
    } catch (e: any) {
      alert(e.message || 'Error al editar');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteSalaryPayment = async () => {
    const p = deleteSalaryModal.payment;
    if (!p) return;
    setSaving(true);
    try {
      const { data, error } = await supabase.rpc('hr_delete_salary_payment', { p_salary_payment_id: p.id });
      if (error) throw error;
      const result = data as { success: boolean; error?: string };
      if (!result.success) throw new Error(result.error);
      setDeleteSalaryModal({ show: false, payment: null });
      fetchExpenses();
      loadData();
    } catch (e: any) {
      alert(e.message || 'Error al eliminar');
    } finally {
      setSaving(false);
    }
  };

  const handleBulkDeletePayments = async () => {
    const ids = Array.from(selectedPayments);
    setSaving(true);
    try {
      for (const id of ids) {
        const { data, error } = await supabase.rpc('hr_delete_salary_payment', { p_salary_payment_id: id });
        if (error) throw error;
        const result = data as { success: boolean; error?: string };
        if (!result.success) throw new Error(result.error);
      }
      setSelectedPayments(new Set());
      fetchExpenses();
      loadData();
    } catch (e: any) {
      alert(e.message || 'Error al eliminar');
    } finally {
      setSaving(false);
    }
  };

  const toggleSelectPayment = (id: string) => {
    setSelectedPayments((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAllPayments = () => {
    const allIds = salaryPayments.map((sp) => sp.id);
    setSelectedPayments((prev) => (prev.size === allIds.length ? new Set() : new Set(allIds)));
  };

  const getCommissionDetailsForPeriod = useCallback(
    async (empId: string): Promise<{ commission_amount: number; invoiced_amount: number; invoices: Array<{ id: string; number: string; total: number; sale_type: string; client_name: string; date: string }> }> => {
      const { data, error } = await supabase.rpc('hr_commission_invoice_details', {
        p_employee_profile_id: empId,
        p_start_date: periodStart,
        p_end_date: periodEnd,
      });
      if (error) throw error;
      const d = data as { commission_amount?: number; invoiced_amount?: number; invoices?: unknown[] } | null;
      return {
        commission_amount: d?.commission_amount ?? 0,
        invoiced_amount: d?.invoiced_amount ?? 0,
        invoices: Array.isArray(d?.invoices) ? (d.invoices as any[]) : [],
      };
    },
    [periodStart, periodEnd]
  );

  const handleCreateSalaryConfirm = async (emp: EmployeeProfile, baseAmount: number, commissionAmount: number) => {
    const total = baseAmount + commissionAmount;
    if (total <= 0) {
      alert('El empleado no tiene sueldo base ni comisión para este periodo');
      return;
    }
    setSaving(true);
    try {
      await supabase.from('salary_payments').insert({
        employee_profile_id: emp.id,
        period_start: periodStart,
        period_end: periodEnd,
        base_amount: baseAmount,
        commission_amount: commissionAmount,
        vacation_deduction: 0,
        other_deductions: 0,
        total_amount: total,
        status: 'pending',
      });
      setCreateSalaryPreviewModal({ show: false, employee: null });
      loadData();
    } catch (e: any) {
      throw e;
    } finally {
      setSaving(false);
    }
  };

  const openEmployeeEdit = (emp?: EmployeeProfile) => {
    setEmployeeModal({ show: true, editing: emp || null });
  };

  const updateLastSalaryReview = async (empId: string) => {
    try {
      await supabase.from('employee_profiles').update({
        last_salary_review_at: new Date().toISOString().slice(0, 10),
        updated_at: new Date().toISOString(),
      }).eq('id', empId);
      loadData();
    } catch (e: any) {
      alert(e.message);
    }
  };

  const downloadReportCsv = async () => {
    try {
      const { data } = await supabase.rpc('hr_list_employees_with_sales', {
        p_start_date: reportStart,
        p_end_date: reportEnd,
      });
      const emps = Array.isArray(data) ? data : [];
      const { data: spData } = await supabase
        .from('salary_payments')
        .select('*, employee_profiles(name)')
        .lte('period_start', reportEnd)
        .gte('period_end', reportStart);
      const spList = spData || [];
      const profilesMap = new Map<string, string>();
      (spList as any[]).forEach((s) => {
        const name = s.employee_profiles?.name || '—';
        profilesMap.set(s.employee_profile_id, name);
      });
      const rows = spList.map((s: any) => ({
        Empleado: profilesMap.get(s.employee_profile_id) || '—',
        'Fecha inicio': s.period_start,
        'Fecha fin': s.period_end,
        'Sueldo base': s.base_amount,
        Comisión: s.commission_amount,
        Total: s.total_amount,
        Estado: s.status === 'paid' ? 'Pagado' : 'Pendiente',
      }));
      if (rows.length === 0) {
        rows.push({ Empleado: 'Sin datos', 'Fecha inicio': reportStart, 'Fecha fin': reportEnd, 'Sueldo base': 0, Comisión: 0, Total: 0, Estado: '' });
      }
      const csv = objectsToCsv(rows, ['Empleado', 'Fecha inicio', 'Fecha fin', 'Sueldo base', 'Comisión', 'Total', 'Estado']);
      downloadCsv(csv, `sueldos-comisiones-${reportStart}-${reportEnd}.csv`);
    } catch (e: any) {
      alert(e.message || 'Error al descargar');
    }
  };

  if (!isAdmin(userRole)) {
    return (
      <div className="p-6 text-center text-gray-500 dark:text-gray-400">
        Solo los administradores pueden acceder a Colaboradores.
      </div>
    );
  }

  if (loading && employees.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <RefreshCw className="h-8 w-8 animate-spin text-indigo-500" />
      </div>
    );
  }

  const tabs = [
    { id: 'empleados' as const, label: 'Empleados', icon: Users },
    { id: 'sueldos' as const, label: 'Sueldos', icon: DollarSign },
    { id: 'comisiones' as const, label: 'Base y comisión', icon: TrendingUp },
    { id: 'vacaciones' as const, label: 'Vacaciones', icon: Calendar },
    { id: 'descargar' as const, label: 'Descargar', icon: Download },
  ];

  const allColaboradores = [...employees].filter((e) => e.is_active).sort((a, b) => a.name.localeCompare(b.name));

  const getNextSalaryReview = (emp: EmployeeProfile): string | null => {
    const base = emp.last_salary_review_at ? parseISO(emp.last_salary_review_at) : null;
    if (!base || !emp.salary_review_frequency_months) return null;
    const next = addMonths(base, emp.salary_review_frequency_months);
    return format(next, 'MMMM yyyy', { locale: es });
  };

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Colaboradores</h1>
        <button onClick={loadData} className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800">
          <RefreshCw className="h-5 w-5" />
        </button>
      </div>

      {salaryReviewDue.length > 0 && (
        <Card className="border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 rounded-2xl">
          <CardContent className="flex items-center gap-4 py-3">
            <AlertTriangle className="h-6 w-6 text-amber-600 dark:text-amber-400 flex-shrink-0" />
            <div className="flex-1">
              <p className="font-medium text-amber-800 dark:text-amber-200">Revisión de sueldo pendiente</p>
              <p className="text-sm text-amber-700 dark:text-amber-300">
                {salaryReviewDue.map((r) => r.name).join(', ')} — corresponde actualizar sueldo base
              </p>
            </div>
            <Button variant="outline" size="sm" onClick={() => setActiveTab('empleados')}>
              Ir a Empleados
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Cards métricas gerenciales */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <div className="rounded-xl border border-gray-200/80 dark:border-gray-600/80 bg-white dark:bg-gray-800/80 p-3.5 flex items-start justify-between gap-2 hover:shadow-md hover:border-indigo-200 dark:hover:border-indigo-800/50 transition-all cursor-default">
          <div className="min-w-0">
            <p className="text-xs font-medium text-gray-500 dark:text-gray-400 truncate">Sueldos período</p>
            <p className="text-lg font-bold text-gray-900 dark:text-white mt-0.5 tabular-nums">{formatCurrency(metricCards.totalNómina)}</p>
          </div>
          <DollarSign className="h-8 w-8 text-indigo-300/80 dark:text-indigo-600/60 flex-shrink-0" />
        </div>
        <div className="rounded-xl border border-amber-200/80 dark:border-amber-800/50 bg-amber-50/50 dark:bg-amber-900/10 p-3.5 flex items-start justify-between gap-2 hover:shadow-md transition-all cursor-default">
          <div className="min-w-0">
            <p className="text-xs font-medium text-amber-700/80 dark:text-amber-400/80 truncate">Pendientes</p>
            <p className="text-lg font-bold text-amber-700 dark:text-amber-400 mt-0.5 tabular-nums">{formatCurrency(metricCards.pendientes)}</p>
            {metricCards.pagosPendientes > 0 && (
              <p className="text-[11px] text-amber-600/70 dark:text-amber-400/70 mt-0.5">{metricCards.pagosPendientes} {metricCards.pagosPendientes === 1 ? 'pago' : 'pagos'}</p>
            )}
          </div>
          <AlertTriangle className="h-8 w-8 text-amber-400/70 dark:text-amber-500/50 flex-shrink-0" />
        </div>
        <div className="rounded-xl border border-emerald-200/80 dark:border-emerald-800/50 bg-emerald-50/50 dark:bg-emerald-900/10 p-3.5 flex items-start justify-between gap-2 hover:shadow-md transition-all cursor-default">
          <div className="min-w-0">
            <p className="text-xs font-medium text-emerald-700/80 dark:text-emerald-400/80 truncate">Pagados</p>
            <p className="text-lg font-bold text-emerald-700 dark:text-emerald-400 mt-0.5 tabular-nums">{formatCurrency(metricCards.pagados)}</p>
            {metricCards.pagosPagados > 0 && (
              <p className="text-[11px] text-emerald-600/70 dark:text-emerald-400/70 mt-0.5">{metricCards.pagosPagados} {metricCards.pagosPagados === 1 ? 'pago' : 'pagos'}</p>
            )}
          </div>
          <Check className="h-8 w-8 text-emerald-400/70 dark:text-emerald-500/50 flex-shrink-0" />
        </div>
        <div className="rounded-xl border border-gray-200/80 dark:border-gray-600/80 bg-white dark:bg-gray-800/80 p-3.5 flex items-start justify-between gap-2 hover:shadow-md hover:border-indigo-200 dark:hover:border-indigo-800/50 transition-all cursor-default">
          <div className="min-w-0">
            <p className="text-xs font-medium text-gray-500 dark:text-gray-400 truncate">Colaboradores</p>
            <p className="text-lg font-bold text-gray-900 dark:text-white mt-0.5 tabular-nums">{metricCards.totalEmpleados}</p>
          </div>
          <Users className="h-8 w-8 text-indigo-300/80 dark:text-indigo-600/60 flex-shrink-0" />
        </div>
        <div className="rounded-xl border border-slate-200/80 dark:border-slate-600/80 bg-white dark:bg-gray-800/80 p-3.5 flex items-start justify-between gap-2 hover:shadow-md hover:border-slate-300 dark:hover:border-slate-500 transition-all cursor-default">
          <div className="min-w-0">
            <p className="text-xs font-medium text-gray-500 dark:text-gray-400 truncate">Pagos en período</p>
            <p className="text-lg font-bold text-slate-700 dark:text-slate-300 mt-0.5 tabular-nums">{metricCards.pagosEnPeríodo}</p>
          </div>
        </div>
      </div>

      <div className="flex gap-2 border-b border-gray-200 dark:border-gray-700 overflow-x-auto">
        {tabs.map((t) => (
          <button
            key={t.id}
            onClick={() => setActiveTab(t.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-t-lg text-sm font-medium transition-colors shrink-0 ${
              activeTab === t.id
                ? 'bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-200 border-b-2 border-indigo-500 -mb-px'
                : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'
            }`}
          >
            <t.icon className="h-4 w-4" />
            {t.label}
          </button>
        ))}
      </div>

      {activeTab === 'empleados' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <p className="text-sm text-gray-500 dark:text-gray-400">{allColaboradores.length} colaboradores activos</p>
            <Button onClick={() => openEmployeeEdit()} icon={<Plus className="h-4 w-4" />} size="sm" className="rounded-xl">
              Agregar
            </Button>
          </div>
          <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/50">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50/80 dark:bg-gray-800/80">
                  <th className="text-left py-3 px-4 font-medium text-gray-600 dark:text-gray-400">Nombre</th>
                  <th className="text-left py-3 px-3 font-medium text-gray-600 dark:text-gray-400">Rol</th>
                  <th className="text-right py-3 px-3 font-medium text-gray-600 dark:text-gray-400">Sueldo base</th>
                  <th className="text-center py-3 px-3 font-medium text-gray-600 dark:text-gray-400">Comisión</th>
                  <th className="text-right py-3 px-3 font-medium text-gray-600 dark:text-gray-400">Mes actual</th>
                  <th className="text-center py-3 px-3 font-medium text-gray-600 dark:text-gray-400">Vacaciones</th>
                  <th className="text-center py-3 px-3 font-medium text-gray-600 dark:text-gray-400">Revisión sueldo</th>
                  <th className="text-center py-3 px-3 font-medium text-gray-600 dark:text-gray-400 w-24">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {allColaboradores.map((emp) => {
                  const vacRest = (emp.vacation_days_per_year || 0) - (vacationDaysTakenByEmployee[emp.id] || 0);
                  const nextReview = getNextSalaryReview(emp);
                  const needsReview = salaryReviewDue.some((r) => r.id === emp.id);
                  return (
                    <tr
                      key={emp.id}
                      className="border-b border-gray-100 dark:border-gray-700/80 hover:bg-gray-50/50 dark:hover:bg-gray-800/30 transition-colors"
                    >
                      <td className="py-2.5 px-4">
                        <span className="font-medium text-gray-900 dark:text-white">{emp.name}</span>
                        {emp.is_independent && (
                          <span className="ml-1.5 text-xs text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 rounded">Indep.</span>
                        )}
                      </td>
                      <td className="py-2.5 px-3 text-gray-600 dark:text-gray-400">
                        {emp.role}
                        {emp.role === 'Vendedor' && emp.email && (
                          <span className="block text-xs text-gray-500 dark:text-gray-500 truncate max-w-[120px]" title={emp.email}>{emp.email}</span>
                        )}
                      </td>
                      <td className="py-2.5 px-3 text-right font-medium text-gray-900 dark:text-white">{formatCurrency(emp.base_salary || 0)}</td>
                      <td className="py-2.5 px-3 text-center">
                        {emp.role === 'Vendedor' && emp.commission_percent ? (
                          <span className="text-gray-600 dark:text-gray-400">{emp.commission_percent}%</span>
                        ) : (
                          <span className="text-gray-300 dark:text-gray-600">—</span>
                        )}
                      </td>
                      <td className="py-2.5 px-3 text-right">
                        {emp.role === 'Vendedor' ? (
                          <div className="flex flex-col items-end">
                            {emp.commission_amount != null && emp.commission_amount > 0 ? (
                              <span className="text-emerald-600 dark:text-emerald-400 font-medium">{formatCurrency(emp.commission_amount)}</span>
                            ) : (
                              <span className="text-gray-400 dark:text-gray-500">—</span>
                            )}
                            {emp.invoiced_amount != null && emp.invoiced_amount > 0 && (
                              <span className="text-xs text-gray-500 dark:text-gray-500">{formatCurrency(emp.invoiced_amount)} fact.</span>
                            )}
                          </div>
                        ) : (
                          <span className="text-gray-400 dark:text-gray-500">—</span>
                        )}
                      </td>
                      <td className="py-2.5 px-3 text-center">
                        <span className={vacRest <= 3 ? 'text-amber-600 dark:text-amber-400 font-medium' : 'text-gray-600 dark:text-gray-400'}>
                          {vacRest}
                        </span>
                        <span className="text-gray-400 dark:text-gray-500 text-xs">/{(emp.vacation_days_per_year || 14)}</span>
                      </td>
                      <td className="py-2.5 px-3 text-center">
                        {nextReview ? (
                          <span className="text-xs text-gray-600 dark:text-gray-400">{nextReview}</span>
                        ) : (
                          <span className="text-gray-300 dark:text-gray-600">—</span>
                        )}
                      </td>
                      <td className="py-2.5 px-3">
                        <div className="flex items-center justify-center gap-1">
                          <button
                            type="button"
                            onClick={() => openEmployeeEdit(emp)}
                            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                            title="Editar"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          {needsReview && (
                            <Button variant="outline" size="sm" onClick={() => updateLastSalaryReview(emp.id)} className="rounded-lg py-1 px-2 text-xs">
                              Revisado
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === 'sueldos' && (
        <div className="space-y-6">
          <div className="flex flex-wrap gap-4 items-center p-4 rounded-2xl bg-gray-50 dark:bg-gray-800/50 border-2 border-gray-100 dark:border-gray-700">
            <label className="flex items-center gap-2">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Período:</span>
              <input
                type="date"
                value={periodStart}
                onChange={(e) => setPeriodStart(e.target.value)}
                className="px-4 py-2 rounded-xl border-2 border-gray-200 dark:border-gray-600 dark:bg-gray-800 dark:text-white focus:border-indigo-500"
              />
              <span className="text-gray-500">a</span>
              <input
                type="date"
                value={periodEnd}
                onChange={(e) => setPeriodEnd(e.target.value)}
                className="px-4 py-2 rounded-xl border-2 border-gray-200 dark:border-gray-600 dark:bg-gray-800 dark:text-white focus:border-indigo-500"
              />
            </label>
            {(() => {
              const now = new Date();
              const prevMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1).toISOString().slice(0, 10);
              const prevMonthEnd = new Date(now.getFullYear(), now.getMonth(), 0).toISOString().slice(0, 10);
              const currMonthStart = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10);
              const currMonthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().slice(0, 10);
              const isPrevMonth = periodStart === prevMonthStart && periodEnd === prevMonthEnd;
              const isCurrMonth = periodStart === currMonthStart && periodEnd === currMonthEnd;
              return (
                <>
                  <button
                    type="button"
                    onClick={() => {
                      setPeriodStart(prevMonthStart);
                      setPeriodEnd(prevMonthEnd);
                    }}
                    className={`px-4 py-2 rounded-xl text-sm font-medium transition-all border-2 ${
                      isPrevMonth
                        ? 'border-indigo-500 bg-indigo-500 text-white shadow-sm ring-2 ring-indigo-200 dark:ring-indigo-800'
                        : 'border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:border-gray-300 dark:hover:border-gray-500'
                    }`}
                  >
                    Mes anterior (a pagar)
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setPeriodStart(currMonthStart);
                      setPeriodEnd(currMonthEnd);
                    }}
                    className={`px-4 py-2 rounded-xl text-sm font-medium transition-all border-2 ${
                      isCurrMonth
                        ? 'border-indigo-500 bg-indigo-500 text-white shadow-sm ring-2 ring-indigo-200 dark:ring-indigo-800'
                        : 'border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:border-gray-300 dark:hover:border-gray-500'
                    }`}
                  >
                    Mes actual (futuros sueldos)
                  </button>
                </>
              );
            })()}
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Los sueldos y comisiones se acumulan en el mes y se pagan del 1 al 5 del mes siguiente. Crear pagos, editar si hace falta, luego marcar como pagados.
          </p>
          {selectedPayments.size > 0 && (
            <div className="flex flex-wrap gap-2 items-center p-4 rounded-2xl bg-indigo-50 dark:bg-indigo-900/20 border-2 border-indigo-200 dark:border-indigo-800">
              <span className="text-sm font-medium text-indigo-800 dark:text-indigo-200">{selectedPayments.size} seleccionado(s)</span>
              <Button size="sm" variant="primary" onClick={() => setBulkMarkPaidModal(true)} className="rounded-xl" disabled={salaryPayments.filter((sp) => selectedPayments.has(sp.id) && sp.status === 'pending').length === 0}>
                Marcar pagados
              </Button>
              <Button size="sm" variant="outline" onClick={handleBulkDeletePayments} className="rounded-xl" disabled={saving}>
                Eliminar
              </Button>
              <Button size="sm" variant="outline" onClick={() => setSelectedPayments(new Set())} className="rounded-xl">
                Deseleccionar
              </Button>
            </div>
          )}
          <div className="overflow-x-auto rounded-2xl border-2 border-gray-100 dark:border-gray-700">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-700">
                  <th className="py-2 px-3 w-10">
                    <input type="checkbox" checked={salaryPayments.length > 0 && selectedPayments.size === salaryPayments.length} onChange={selectAllPayments} className="rounded" />
                  </th>
                  <th className="text-left py-2 px-3">Empleado</th>
                  <th className="text-right py-2 px-3">Período</th>
                  <th className="text-right py-2 px-3">Base</th>
                  <th className="text-right py-2 px-3">Comisión</th>
                  <th className="text-right py-2 px-3">Total</th>
                  <th className="text-center py-2 px-3">Estado</th>
                  <th className="text-center py-2 px-3">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {salaryPayments.map((sp) => (
                  <tr
                    key={sp.id}
                    className="border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50 cursor-pointer transition-colors"
                    onClick={() => setEditSalaryModal({ show: true, payment: sp })}
                  >
                    <td className="py-2 px-3" onClick={(e) => e.stopPropagation()}>
                      <input type="checkbox" checked={selectedPayments.has(sp.id)} onChange={() => toggleSelectPayment(sp.id)} className="rounded" />
                    </td>
                    <td className="py-2 px-3">{sp.employee_name}</td>
                    <td className="text-right py-2 px-3">
                      {format(new Date(sp.period_start), 'd/M/yyyy', { locale: es })} - {format(new Date(sp.period_end), 'd/M/yyyy', { locale: es })}
                    </td>
                    <td className="text-right py-2 px-3">{formatCurrency(sp.base_amount)}</td>
                    <td className="text-right py-2 px-3">{formatCurrency(sp.commission_amount)}</td>
                    <td className="text-right py-2 px-3 font-medium">{formatCurrency(sp.total_amount)}</td>
                    <td className="text-center py-2 px-3">
                      {sp.status === 'paid' ? (
                        <span className="text-green-600 dark:text-green-400 flex items-center justify-center gap-1">
                          <Check className="h-4 w-4" /> Pagado
                        </span>
                      ) : (
                        <span className="text-amber-600 dark:text-amber-400">Pendiente</span>
                      )}
                    </td>
                    <td className="text-center py-2 px-3" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-center gap-1 flex-wrap">
                        {sp.status === 'pending' && (
                          <>
                            <Button size="sm" variant="primary" onClick={() => setMarkPaidModal({ show: true, payment: sp })} className="rounded-xl">
                              Pagar
                            </Button>
                            <button type="button" onClick={() => setEditSalaryModal({ show: true, payment: sp })} className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600">
                              <Edit className="h-4 w-4" />
                            </button>
                          </>
                        )}
                        <button type="button" onClick={() => setDeleteSalaryModal({ show: true, payment: sp })} className="p-1.5 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 text-red-600" title="Eliminar">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {salaryPayments.length === 0 && (
              <p className="text-center py-8 text-gray-500 dark:text-gray-400">
                No hay pagos. Creá uno desde un empleado en la pestaña Empleados.
              </p>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            {employees.filter((e) => e.is_active).map((emp) => (
              <Button
                key={emp.id}
                variant="outline"
                size="sm"
                onClick={() => setCreateSalaryPreviewModal({ show: true, employee: emp })}
                disabled={saving}
                className="rounded-xl"
              >
                + Crear pago {emp.name}
              </Button>
            ))}
          </div>
        </div>
      )}

      {activeTab === 'comisiones' && (
        <div className="space-y-6">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setCommissionYear((y) => y - 1)}
                className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700/80 text-gray-600 dark:text-gray-300 transition-colors"
                aria-label="Año anterior"
              >
                ←
              </button>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white min-w-[5rem] text-center">{commissionYear}</h2>
              <button
                type="button"
                onClick={() => setCommissionYear((y) => y + 1)}
                className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700/80 text-gray-600 dark:text-gray-300 transition-colors"
                aria-label="Año siguiente"
              >
                →
              </button>
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Base mensual y comisión acumulada del año. Abrí el detalle para ver ventas mes a mes.
            </p>
          </div>

          <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/50">
            {commissionLoading ? (
              <div className="flex items-center justify-center py-16">
                <RefreshCw className="h-6 w-6 animate-spin text-indigo-500" />
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50/80 dark:bg-gray-800/80">
                    <th className="text-left py-3 px-4 font-medium text-gray-600 dark:text-gray-400">Colaborador</th>
                    <th className="text-left py-3 px-3 font-medium text-gray-600 dark:text-gray-400">Rol</th>
                    <th className="text-right py-3 px-3 font-medium text-gray-600 dark:text-gray-400">Base mensual</th>
                    <th className="text-center py-3 px-3 font-medium text-gray-600 dark:text-gray-400">%</th>
                    <th className="text-right py-3 px-3 font-medium text-gray-600 dark:text-gray-400">Facturado {commissionYear}</th>
                    <th className="text-right py-3 px-3 font-medium text-gray-600 dark:text-gray-400">Comisión {commissionYear}</th>
                    <th className="text-right py-3 px-3 font-medium text-gray-600 dark:text-gray-400">Base + comisión</th>
                    <th className="text-center py-3 px-3 font-medium text-gray-600 dark:text-gray-400 w-28">Detalle</th>
                  </tr>
                </thead>
                <tbody>
                  {[...commissionEmployees]
                    .filter((e) => e.is_active)
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map((emp) => {
                      const base = Number(emp.base_salary) || 0;
                      const commission = Number(emp.commission_amount) || 0;
                      const invoiced = Number(emp.invoiced_amount) || 0;
                      const annualBase = base * 12;
                      const total = annualBase + commission;
                      return (
                        <tr
                          key={emp.id}
                          className="border-b border-gray-100 dark:border-gray-700/80 hover:bg-gray-50/50 dark:hover:bg-gray-800/30 transition-colors"
                        >
                          <td className="py-2.5 px-4 font-medium text-gray-900 dark:text-white">{emp.name}</td>
                          <td className="py-2.5 px-3 text-gray-600 dark:text-gray-400">{emp.role}</td>
                          <td className="py-2.5 px-3 text-right font-medium text-gray-900 dark:text-white tabular-nums">
                            {formatCurrency(base)}
                          </td>
                          <td className="py-2.5 px-3 text-center text-gray-600 dark:text-gray-400">
                            {emp.role === 'Vendedor' && emp.commission_percent ? (
                              `${emp.commission_percent}%`
                            ) : (
                              <span className="text-gray-300 dark:text-gray-600">—</span>
                            )}
                          </td>
                          <td className="py-2.5 px-3 text-right tabular-nums text-gray-900 dark:text-white">
                            {emp.role === 'Vendedor' && invoiced > 0 ? (
                              formatCurrency(invoiced)
                            ) : (
                              <span className="text-gray-300 dark:text-gray-600">—</span>
                            )}
                          </td>
                          <td className="py-2.5 px-3 text-right tabular-nums font-medium text-emerald-600 dark:text-emerald-400">
                            {emp.role === 'Vendedor' && commission > 0 ? (
                              formatCurrency(commission)
                            ) : (
                              <span className="text-gray-300 dark:text-gray-600">—</span>
                            )}
                          </td>
                          <td className="py-2.5 px-3 text-right tabular-nums font-semibold text-indigo-700 dark:text-indigo-300">
                            {formatCurrency(total)}
                          </td>
                          <td className="py-2.5 px-3 text-center">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => openCommissionDetail(emp)}
                              className="rounded-lg py-1 px-2"
                              icon={<Eye className="h-3.5 w-3.5" />}
                              disabled={emp.role !== 'Vendedor'}
                              title={emp.role === 'Vendedor' ? 'Ver ventas mes a mes' : 'Solo aplica a vendedores'}
                            >
                              Ver
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  {commissionEmployees.filter((e) => e.is_active).length === 0 && (
                    <tr>
                      <td colSpan={8} className="py-12 text-center text-gray-500 dark:text-gray-400">
                        No hay colaboradores activos.
                      </td>
                    </tr>
                  )}
                </tbody>
                {commissionEmployees.filter((e) => e.is_active).length > 0 && (
                  <tfoot>
                    <tr className="border-t-2 border-gray-200 dark:border-gray-600 bg-gray-50/80 dark:bg-gray-800/80">
                      <td colSpan={4} className="py-3 px-4 font-semibold text-gray-700 dark:text-gray-300">
                        Totales {commissionYear}
                      </td>
                      <td className="py-3 px-3 text-right font-semibold tabular-nums text-gray-900 dark:text-white">
                        {formatCurrency(
                          commissionEmployees
                            .filter((e) => e.is_active)
                            .reduce((s, e) => s + (Number(e.invoiced_amount) || 0), 0)
                        )}
                      </td>
                      <td className="py-3 px-3 text-right font-semibold tabular-nums text-emerald-600 dark:text-emerald-400">
                        {formatCurrency(
                          commissionEmployees
                            .filter((e) => e.is_active)
                            .reduce((s, e) => s + (Number(e.commission_amount) || 0), 0)
                        )}
                      </td>
                      <td className="py-3 px-3 text-right font-semibold tabular-nums text-indigo-700 dark:text-indigo-300">
                        {formatCurrency(
                          commissionEmployees
                            .filter((e) => e.is_active)
                            .reduce(
                              (s, e) =>
                                s + (Number(e.base_salary) || 0) * 12 + (Number(e.commission_amount) || 0),
                              0
                            )
                        )}
                      </td>
                      <td />
                    </tr>
                  </tfoot>
                )}
              </table>
            )}
          </div>
        </div>
      )}

      {activeTab === 'vacaciones' && (
        <div className="space-y-6">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setVacationYear((y) => y - 1)}
                className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700/80 text-gray-600 dark:text-gray-300 transition-colors"
                aria-label="Año anterior"
              >
                ←
              </button>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white min-w-[5rem] text-center">{vacationYear}</h2>
              <button
                type="button"
                onClick={() => setVacationYear((y) => y + 1)}
                className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700/80 text-gray-600 dark:text-gray-300 transition-colors"
                aria-label="Año siguiente"
              >
                →
              </button>
            </div>
            <Button onClick={() => setAddVacationModal(true)} icon={<Plus className="h-4 w-4" />} className="rounded-xl">
              Registrar vacaciones
            </Button>
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Los días se calculan automáticamente por período (inicio–fin). Se descuentan del disponible del año calendario.
          </p>
          <div className="overflow-x-auto rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/50 shadow-sm">
            <table className="w-full text-sm min-w-[640px]">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/80">
                  <th className="text-left py-3 px-4 font-medium text-gray-700 dark:text-gray-300 sticky left-0 bg-gray-50 dark:bg-gray-800/80 z-10">
                    Colaborador
                  </th>
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((m) => (
                    <th key={m} className="text-center py-3 px-2 font-medium text-gray-600 dark:text-gray-400 w-12">
                      {format(new Date(vacationYear, m - 1, 1), 'MMM', { locale: es })}
                    </th>
                  ))}
                  <th className="text-center py-3 px-3 font-semibold text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700/50 w-14">
                    Total
                  </th>
                  <th className="text-center py-3 px-3 font-semibold text-emerald-700 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/20 w-20">
                    Restantes
                  </th>
                </tr>
              </thead>
              <tbody>
                {vacationMatrix.employees.length === 0 ? (
                  <tr>
                    <td colSpan={15} className="py-12 text-center text-gray-500 dark:text-gray-400">
                      No hay colaboradores activos. Agregá uno en la pestaña Empleados.
                    </td>
                  </tr>
                ) : (
                  vacationMatrix.employees.map((emp) => {
                    const row = vacationMatrix.matrix[emp.id] || {};
                    const totalTaken = Object.values(row).reduce((s, d) => s + d, 0);
                    const available = emp.vacation_days_per_year || 14;
                    const remaining = Math.max(0, available - totalTaken);
                    return (
                      <tr
                        key={emp.id}
                        className="border-b border-gray-100 dark:border-gray-700/80 hover:bg-gray-50/50 dark:hover:bg-gray-800/30 transition-colors"
                      >
                        <td className="py-2.5 px-4 font-medium text-gray-900 dark:text-white sticky left-0 bg-white dark:bg-gray-800/50 z-10">
                          {emp.name}
                        </td>
                        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((m) => {
                          const d = row[m] || 0;
                          return (
                            <td key={m} className="text-center py-2 px-2">
                              {d > 0 ? (
                                <span className="inline-flex items-center justify-center min-w-[1.5rem] h-6 px-1.5 rounded-md bg-sky-100 dark:bg-sky-900/40 text-sky-700 dark:text-sky-300 font-medium text-xs">
                                  {d}
                                </span>
                              ) : (
                                <span className="text-gray-300 dark:text-gray-600">—</span>
                              )}
                            </td>
                          );
                        })}
                        <td className="text-center py-2 px-3 font-medium text-gray-700 dark:text-gray-300 bg-gray-50/80 dark:bg-gray-700/30">
                          {totalTaken > 0 ? totalTaken : '—'}
                        </td>
                        <td className="text-center py-2 px-3 font-semibold bg-emerald-50/50 dark:bg-emerald-900/10">
                          <span className={remaining <= 3 ? 'text-amber-600 dark:text-amber-400' : 'text-emerald-600 dark:text-emerald-400'}>
                            {remaining}
                          </span>
                          <span className="text-gray-500 dark:text-gray-400 text-xs ml-0.5">/ {available}</span>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
          {vacationEntries.length > 0 && (
            <div className="mt-6">
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Registros de vacaciones</h3>
              <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/80">
                      <th className="text-left py-2 px-4 font-medium text-gray-600 dark:text-gray-400">Colaborador</th>
                      <th className="text-left py-2 px-4 font-medium text-gray-600 dark:text-gray-400">Inicio</th>
                      <th className="text-left py-2 px-4 font-medium text-gray-600 dark:text-gray-400">Fin</th>
                      <th className="text-center py-2 px-4 font-medium text-gray-600 dark:text-gray-400">Días</th>
                      <th className="text-right py-2 px-4 font-medium text-gray-600 dark:text-gray-400 w-28">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {vacationEntries
                      .sort((a, b) => a.start_date.localeCompare(b.start_date) || (a.employee_name || '').localeCompare(b.employee_name || ''))
                      .map((ve) => (
                        <tr key={ve.id} className="border-b border-gray-100 dark:border-gray-700/80 hover:bg-gray-50/50 dark:hover:bg-gray-800/30">
                          <td className="py-2 px-4 text-gray-900 dark:text-white">{ve.employee_name}</td>
                          <td className="py-2 px-4 text-gray-600 dark:text-gray-400">{format(parseISO(ve.start_date), 'd MMM yyyy', { locale: es })}</td>
                          <td className="py-2 px-4 text-gray-600 dark:text-gray-400">{format(parseISO(ve.end_date), 'd MMM yyyy', { locale: es })}</td>
                          <td className="py-2 px-4 text-center">
                            <span className="inline-flex items-center justify-center min-w-[1.5rem] h-6 px-1.5 rounded-md bg-sky-100 dark:bg-sky-900/40 text-sky-700 dark:text-sky-300 font-medium text-xs">
                              {ve.days_taken}
                            </span>
                          </td>
                          <td className="py-2 px-4 text-right">
                            <div className="flex justify-end gap-1">
                              <Button size="sm" variant="outline" onClick={() => setEditVacationModal(ve)} className="rounded-lg py-1 px-2">
                                <Edit className="h-3.5 w-3.5" />
                              </Button>
                              <button
                                type="button"
                                onClick={() => setDeleteVacationModal(ve)}
                                className="p-2 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 text-red-600 dark:text-red-400"
                                title="Eliminar"
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'descargar' && (
        <Card className="rounded-2xl border-2 border-gray-100 dark:border-gray-700">
          <CardContent className="p-6 space-y-4">
            <p className="text-gray-600 dark:text-gray-400">Descargá sueldos y comisiones por período en CSV.</p>
            <div className="flex flex-wrap gap-4 items-center p-4 rounded-2xl bg-gray-50 dark:bg-gray-800/50">
              <label className="flex items-center gap-2">
                <span className="text-sm font-medium">Desde:</span>
                <input
                  type="date"
                  value={reportStart}
                  onChange={(e) => setReportStart(e.target.value)}
                  className="px-4 py-2 rounded-xl border-2 border-gray-200 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
                />
              </label>
              <label className="flex items-center gap-2">
                <span className="text-sm font-medium">Hasta:</span>
                <input
                  type="date"
                  value={reportEnd}
                  onChange={(e) => setReportEnd(e.target.value)}
                  className="px-4 py-2 rounded-xl border-2 border-gray-200 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
                />
              </label>
              <Button onClick={downloadReportCsv} icon={<Download className="h-4 w-4" />} className="rounded-xl">
                Descargar CSV
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <EmployeeModal
        isOpen={employeeModal.show}
        onClose={() => setEmployeeModal({ show: false, editing: null })}
        onSave={handleSaveEmployee}
        initialData={
          employeeModal.editing
            ? {
                name: employeeModal.editing.name,
                email: employeeModal.editing.email ?? undefined,
                role: employeeModal.editing.role,
                base_salary: employeeModal.editing.base_salary ?? 0,
                commission_percent: employeeModal.editing.commission_percent ?? 0,
                salary_review_frequency_months: employeeModal.editing.salary_review_frequency_months ?? 3,
                vacation_days_per_year: employeeModal.editing.vacation_days_per_year ?? 14,
                is_active: employeeModal.editing.is_active ?? true,
                is_independent: employeeModal.editing.is_independent ?? false,
                user_id: employeeModal.editing.user_id ?? undefined,
              }
            : undefined
        }
      />
      <MarkPaidModal
        isOpen={markPaidModal.show}
        onClose={() => setMarkPaidModal({ show: false, payment: null })}
        onConfirm={handleMarkPaid}
        payment={markPaidModal.payment}
      />
      <AddVacationModal
        isOpen={addVacationModal || Boolean(editVacationModal)}
        onClose={() => { setAddVacationModal(false); setEditVacationModal(null); }}
        onSave={handleAddVacation}
        onUpdate={handleEditVacation}
        employees={employees.filter((e) => e.is_active).map((e) => ({ id: e.id, name: e.name }))}
        initialEntry={editVacationModal ? { id: editVacationModal.id, employee_profile_id: editVacationModal.employee_profile_id, start_date: editVacationModal.start_date, end_date: editVacationModal.end_date } : null}
      />
      <DeleteConfirmModal
        isOpen={Boolean(deleteVacationModal)}
        onClose={() => setDeleteVacationModal(null)}
        onConfirm={handleDeleteVacation}
        title="Eliminar vacaciones"
        message={deleteVacationModal ? `¿Eliminar las vacaciones de ${deleteVacationModal.employee_name} (${format(parseISO(deleteVacationModal.start_date), 'd MMM', { locale: es })} – ${format(parseISO(deleteVacationModal.end_date), 'd MMM yyyy', { locale: es })}, ${deleteVacationModal.days_taken} días)?` : ''}
      />
      <EditSalaryPaymentModal
        isOpen={editSalaryModal.show}
        onClose={() => setEditSalaryModal({ show: false, payment: null })}
        onSave={handleEditSalaryPayment}
        payment={editSalaryModal.payment}
      />
      <CreateSalaryPreviewModal
        isOpen={createSalaryPreviewModal.show}
        onClose={() => setCreateSalaryPreviewModal({ show: false, employee: null })}
        onConfirm={handleCreateSalaryConfirm}
        employee={createSalaryPreviewModal.employee}
        periodStart={periodStart}
        periodEnd={periodEnd}
        getCommissionDetailsForPeriod={getCommissionDetailsForPeriod}
      />
      <DeleteConfirmModal
        isOpen={deleteSalaryModal.show}
        onClose={() => setDeleteSalaryModal({ show: false, payment: null })}
        onConfirm={handleDeleteSalaryPayment}
        title="Eliminar pago"
        message={deleteSalaryModal.payment ? `¿Eliminar el pago de ${deleteSalaryModal.payment.employee_name} (${formatCurrency(deleteSalaryModal.payment.total_amount)})? Solo se pueden eliminar pagos pendientes.` : ''}
      />
      <BulkMarkPaidModal
        isOpen={bulkMarkPaidModal}
        onClose={() => setBulkMarkPaidModal(false)}
        onConfirm={handleBulkMarkPaid}
        count={Array.from(selectedPayments).filter((id) => salaryPayments.find((sp) => sp.id === id)?.status === 'pending').length}
        totalAmount={Array.from(selectedPayments).reduce((sum, id) => {
          const p = salaryPayments.find((sp) => sp.id === id);
          return sum + (p?.status === 'pending' ? (p.total_amount || 0) : 0);
        }, 0)}
        defaultDate={(() => {
          const pending = Array.from(selectedPayments)
            .map((id) => salaryPayments.find((sp) => sp.id === id))
            .filter((p): p is SalaryPayment => !!p && p.status === 'pending');
          if (pending.length === 0) return periodEnd;
          return pending.reduce((max, p) => (p.period_end > max ? p.period_end : max), pending[0].period_end);
        })()}
      />
      <CommissionDetailModal
        isOpen={commissionDetailModal.show}
        onClose={() => {
          setCommissionDetailModal({ show: false, employee: null });
          setCommissionDetailInvoices([]);
        }}
        employee={commissionDetailModal.employee}
        year={commissionYear}
        loading={commissionDetailLoading}
        invoices={commissionDetailInvoices}
        commissionPercent={Number(commissionDetailModal.employee?.commission_percent) || 0}
      />
    </div>
  );
};

export default Colaboradores;
